/**
 * SF-4729, SF-4746: a build whose draft failed, for any reason, can be drafted again with what it
 * holds when sf-api says so (`can_draft_again`): POST .../draft. sf-api answers the build as it
 * now is; 409 DEMO_BUILD_BUSY while Eva is still finishing the draft that failed (the build is left
 * as it was), 409 DEMO_BUILD_NOT_AWAITING when it was already asked (another tab), and 409
 * DEMO_BUILD_CANNOT_DRAFT_AGAIN when its call files are no longer held.
 */
import { useState } from 'react';
import { Alert, Button, Space } from 'antd';
import {
  DEMO_BUILD_BUSY,
  DEMO_BUILD_CANNOT_DRAFT_AGAIN,
  DEMO_BUILD_NOT_AWAITING,
  demoErrorCode,
  demoErrorMessage,
  startDraftArgs,
  useDemoMutation,
  type DemoBuild,
} from '../api/demoBuildsClient';

const EVA_STILL_FINISHING = 'Eva is still finishing, try again in a moment';
const FILES_NOT_HELD = "This demo's call files are no longer held; start a new demo";

export interface DraftAgainProps {
  buildId: string;
  /** The answer for this build (or nothing): the page puts it in its cache and reads again. */
  onBuild: (buildId: string, answer?: DemoBuild) => Promise<void> | void;
}

/** Mount it keyed by the build id, so an answer for one build never shows on another. */
export const DraftAgain: React.FC<DraftAgainProps> = ({ buildId, onBuild }) => {
  const draft = useDemoMutation<DemoBuild>();
  const [error, setError] = useState<string | null>(null);
  // Once sf-api says the files are gone, pressing again cannot work. The build is not read again
  // here: it would drop this panel, and with it the reason.
  const [notHeld, setNotHeld] = useState(false);

  const onDraftAgain = async () => {
    setError(null);
    try {
      const { data } = await draft.mutateAsync(startDraftArgs(buildId));
      await onBuild(buildId, data);
    } catch (err) {
      const code = demoErrorCode(err);
      if (code === DEMO_BUILD_NOT_AWAITING) {
        await onBuild(buildId);
        return;
      }
      if (code === DEMO_BUILD_CANNOT_DRAFT_AGAIN) {
        setNotHeld(true);
        setError(demoErrorMessage(err, FILES_NOT_HELD));
        return;
      }
      setError(
        code === DEMO_BUILD_BUSY
          ? EVA_STILL_FINISHING
          : `Eva could not be asked to draft this demo again: ${demoErrorMessage(err, 'try again.')}`,
      );
      if (code === DEMO_BUILD_BUSY) await onBuild(buildId);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        type="warning"
        showIcon
        message="Eva could not finish drafting this demo"
        description="Draft it again with the same web address, area, notes and call files."
        action={
          <Button
            type="primary"
            loading={draft.isLoading}
            disabled={notHeld}
            onClick={() => void onDraftAgain()}
          >
            Draft again
          </Button>
        }
      />
      {error && <Alert type="error" showIcon message={error} />}
    </Space>
  );
};
